// domain/qr.ts — encoder QR ASLI (byte mode · ECC level M · versi 1–7), scannable & spec-compliant.
// Port persis dari v0.18 (Batch 37 — fix bit Format Information MSB-first; terverifikasi
// byte-identik dgn library standar `qrcode`). Murni: qrSVG(text, px) → string SVG.
// Reed–Solomon (GF(256), 0x11d), format/version BCH, 8 mask + penalty (pilih terbaik).

const EXP = new Array<number>(256);
const LOG = new Array<number>(256);
(() => { let x = 1; for (let i = 0; i < 255; i++) { EXP[i] = x; LOG[x] = i; x <<= 1; if (x & 0x100) x ^= 0x11d; } EXP[255] = EXP[0]!; })();

const mul = (a: number, b: number): number => (a === 0 || b === 0 ? 0 : EXP[(LOG[a]! + LOG[b]!) % 255]!);

function genPoly(deg: number): number[] {
  let g = [1];
  for (let i = 0; i < deg; i++) {
    const ng = new Array<number>(g.length + 1).fill(0);
    for (let j = 0; j < g.length; j++) { ng[j] = ng[j]! ^ g[j]!; ng[j + 1] = ng[j + 1]! ^ mul(g[j]!, EXP[i]!); }
    g = ng;
  }
  return g;
}

function ecc(data: number[], deg: number): number[] {
  const gen = genPoly(deg);
  const res = data.slice();
  for (let i = 0; i < deg; i++) res.push(0);
  for (let i = 0; i < data.length; i++) {
    const c = res[i]!;
    if (c !== 0) for (let j = 0; j < gen.length; j++) res[i + j] = res[i + j]! ^ mul(gen[j]!, c);
  }
  return res.slice(data.length);
}

interface VerInfo { ec: number; blk: number; tot: number }
const QR_M: Record<number, VerInfo> = {
  1: { ec: 10, blk: 1, tot: 26 }, 2: { ec: 16, blk: 1, tot: 44 }, 3: { ec: 26, blk: 1, tot: 70 },
  4: { ec: 18, blk: 2, tot: 100 }, 5: { ec: 24, blk: 2, tot: 134 }, 6: { ec: 16, blk: 4, tot: 172 }, 7: { ec: 18, blk: 4, tot: 196 },
};
const QR_CAP: Record<number, number> = { 1: 14, 2: 26, 3: 42, 4: 62, 5: 84, 6: 106, 7: 122 };
const QR_ALIGN: Record<number, number[]> = { 1: [], 2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30], 6: [6, 34], 7: [6, 22, 38] };

const bit = (x: number, i: number): number => (x >>> i) & 1;

interface QrMatrix { size: number; mods: number[][] }

function genMatrix(text: string): QrMatrix | null {
  const bytes: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const cc = text.charCodeAt(i);
    if (cc < 128) bytes.push(cc);
    else if (cc < 2048) { bytes.push(192 | (cc >> 6), 128 | (cc & 63)); }
    else { bytes.push(224 | (cc >> 12), 128 | ((cc >> 6) & 63), 128 | (cc & 63)); }
  }
  let ver = 0;
  for (let v = 1; v <= 7; v++) { if (bytes.length <= QR_CAP[v]!) { ver = v; break; } }
  if (!ver) return null;
  const info = QR_M[ver]!;
  const dataPerBlock = info.tot / info.blk - info.ec, totalData = dataPerBlock * info.blk;
  const bits: number[] = [];
  const pb = (val: number, len: number): void => { for (let i = len - 1; i >= 0; i--) bits.push((val >> i) & 1); };
  pb(4, 4); pb(bytes.length, 8);
  for (let i = 0; i < bytes.length; i++) pb(bytes[i]!, 8);
  const dataBits = totalData * 8, term = Math.min(4, dataBits - bits.length);
  for (let i = 0; i < term; i++) bits.push(0);
  while (bits.length % 8 !== 0) bits.push(0);
  const cw: number[] = [];
  for (let i = 0; i < bits.length; i += 8) { let b = 0; for (let j = 0; j < 8; j++) b = (b << 1) | bits[i + j]!; cw.push(b); }
  const pad = [0xEC, 0x11]; let pi = 0;
  while (cw.length < totalData) { cw.push(pad[pi & 1]!); pi++; }
  const blocks: number[][] = [], eblocks: number[][] = [];
  for (let b = 0; b < info.blk; b++) { const bl = cw.slice(b * dataPerBlock, (b + 1) * dataPerBlock); blocks.push(bl); eblocks.push(ecc(bl, info.ec)); }
  const fin: number[] = [];
  for (let i = 0; i < dataPerBlock; i++) for (let b = 0; b < info.blk; b++) fin.push(blocks[b]![i]!);
  for (let i = 0; i < info.ec; i++) for (let b = 0; b < info.blk; b++) fin.push(eblocks[b]![i]!);

  const size = 17 + 4 * ver;
  const m: number[][] = [], fn: boolean[][] = [];
  for (let r = 0; r < size; r++) { m.push(new Array<number>(size).fill(0)); fn.push(new Array<boolean>(size).fill(false)); }
  const setF = (r: number, c: number, v: number | boolean): void => { if (r < 0 || c < 0 || r >= size || c >= size) return; m[r]![c] = v ? 1 : 0; fn[r]![c] = true; };
  const finder = (rr: number, cc: number): void => {
    for (let r = -1; r <= 7; r++) for (let c = -1; c <= 7; c++) {
      const on = (r >= 0 && r <= 6 && (c === 0 || c === 6)) || (c >= 0 && c <= 6 && (r === 0 || r === 6)) || (r >= 2 && r <= 4 && c >= 2 && c <= 4);
      setF(rr + r, cc + c, on);
    }
  };
  finder(0, 0); finder(0, size - 7); finder(size - 7, 0);
  for (let i = 8; i < size - 8; i++) { if (!fn[6]![i]) setF(6, i, i % 2 === 0); if (!fn[i]![6]) setF(i, 6, i % 2 === 0); }
  const ap = QR_ALIGN[ver]!;
  for (let ai = 0; ai < ap.length; ai++) for (let aj = 0; aj < ap.length; aj++) {
    const ar = ap[ai]!, ac = ap[aj]!;
    if ((ar === 6 && ac === 6) || (ar === 6 && ac === ap[ap.length - 1]!) || (ar === ap[ap.length - 1]! && ac === 6)) continue;
    for (let r = -2; r <= 2; r++) for (let c = -2; c <= 2; c++) setF(ar + r, ac + c, Math.max(Math.abs(r), Math.abs(c)) !== 1);
  }
  setF(size - 8, 8, 1);
  for (let i = 0; i <= 8; i++) { if (!fn[8]![i]) setF(8, i, 0); if (!fn[i]![8]) setF(i, 8, 0); }
  for (let i = 0; i < 8; i++) { setF(8, size - 1 - i, 0); setF(size - 1 - i, 8, 0); }
  if (ver >= 7) { for (let i = 0; i < 18; i++) { const a = size - 11 + i % 3, b = Math.floor(i / 3); setF(a, b, 0); setF(b, a, 0); } }

  let di = 0;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < size; vert++) for (let j = 0; j < 2; j++) {
      const x = right - j, up = ((right + 1) & 2) === 0, y = up ? size - 1 - vert : vert;
      if (!fn[y]![x]) { const b = di < fin.length * 8 ? bit(fin[di >> 3]!, 7 - (di & 7)) : 0; m[y]![x] = b; di++; }
    }
  }
  const maskf = (mk: number, y: number, x: number): boolean => {
    switch (mk) {
      case 0: return (x + y) % 2 === 0; case 1: return y % 2 === 0; case 2: return x % 3 === 0; case 3: return (x + y) % 3 === 0;
      case 4: return (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; case 5: return (x * y) % 2 + (x * y) % 3 === 0;
      case 6: return ((x * y) % 2 + (x * y) % 3) % 2 === 0; default: return ((x + y) % 2 + (x * y) % 3) % 2 === 0;
    }
  };
  const penalty = (mm: number[][]): number => {
    let p = 0, dark = 0;
    for (let i = 0; i < size; i++) {
      let run = 1;
      for (let j = 1; j < size; j++) { if (mm[i]![j] === mm[i]![j - 1]) run++; else { if (run >= 5) p += 3 + (run - 5); run = 1; } } if (run >= 5) p += 3 + (run - 5);
      run = 1;
      for (let j = 1; j < size; j++) { if (mm[j]![i] === mm[j - 1]![i]) run++; else { if (run >= 5) p += 3 + (run - 5); run = 1; } } if (run >= 5) p += 3 + (run - 5);
    }
    for (let i = 0; i < size - 1; i++) for (let j = 0; j < size - 1; j++) { const v = mm[i]![j]; if (v === mm[i]![j + 1] && v === mm[i + 1]![j] && v === mm[i + 1]![j + 1]) p += 3; }
    for (let i = 0; i < size; i++) for (let j = 0; j < size; j++) if (mm[i]![j]) dark++;
    const pct = dark * 100 / (size * size); p += Math.floor(Math.abs(pct - 50) / 5) * 10; return p;
  };

  let best: number[][] | null = null, bestP = 1e9;
  for (let mk = 0; mk < 8; mk++) {
    const mm = m.map((row) => row.slice());
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) if (!fn[y]![x] && maskf(mk, y, x)) mm[y]![x] = mm[y]![x]! ^ 1;
    const data = (0 << 3) | mk; let rem = data;
    for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    let fbits = ((data << 10) | rem) ^ 0x5412;
    let frev = 0; for (let i = 0; i < 15; i++) frev |= ((fbits >> i) & 1) << (14 - i); fbits = frev;
    for (let i = 0; i <= 5; i++) mm[8]![i] = bit(fbits, i);
    mm[8]![7] = bit(fbits, 6); mm[8]![8] = bit(fbits, 7); mm[7]![8] = bit(fbits, 8);
    for (let i = 9; i < 15; i++) mm[14 - i]![8] = bit(fbits, i);
    for (let i = 0; i < 8; i++) mm[size - 1 - i]![8] = bit(fbits, i);
    for (let i = 8; i < 15; i++) mm[8]![size - 15 + i] = bit(fbits, i);
    mm[size - 8]![8] = 1;
    if (ver >= 7) {
      const vr = ver; let vrem = vr;
      for (let i = 0; i < 12; i++) vrem = (vrem << 1) ^ ((vrem >>> 11) * 0x1f25);
      const vbits = (vr << 12) | vrem;
      for (let i = 0; i < 18; i++) { const bt = bit(vbits, i), a = size - 11 + i % 3, b = Math.floor(i / 3); mm[a]![b] = bt; mm[b]![a] = bt; }
    }
    const pen = penalty(mm); if (pen < bestP) { bestP = pen; best = mm; }
  }
  return best ? { size, mods: best } : null;
}

/** SVG QR scannable (fallback: string kosong bila gagal encode). px = ukuran modul (px). */
export function qrSVG(text: string, px = 4): string {
  const q = ((): QrMatrix | null => { try { return genMatrix(String(text ?? '')); } catch { return null; } })();
  if (!q) return '';
  const n = q.size, quiet = 4, dim = (n + quiet * 2) * px;
  let r = '';
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) if (q.mods[y]![x]) r += `<rect x="${(x + quiet) * px}" y="${(y + quiet) * px}" width="${px}" height="${px}"/>`;
  return `<svg width="${dim}" height="${dim}" viewBox="0 0 ${dim} ${dim}" xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges"><rect width="${dim}" height="${dim}" fill="#fff"/><g fill="#000">${r}</g></svg>`;
}
